import * as Joi from 'joi';
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToMany,
	ManyToOne,
	OneToMany,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { GenPartial, momentTransformer } from 'typeormgen';
import { INT_EXT, SET_TYPES } from '../enums/enums';
import { displayIntExt } from '../enums/enums.utils';
import EpisodicBase from '../episodic-base.entity';
import Episodic from '../episodics/episodic.entity';
import { Indexable } from '../indexable';
import SetDate from '../set-dates/set-date.entity';
import SetPicture from '../set-pictures/set-picture.entity';
import SetPieceSet from '../set-piece-sets/set-piece-set.entity';
import SetPiece from '../set-pieces/set-piece.entity';
import SetProd from '../set-prods/set-prod.entity';
import SetScene from '../set-scenes/set-scene.entity';
import { SetESDoc } from './set.interfaces';

@Entity('sets')
export default class Set extends EpisodicBase implements Indexable<SetESDoc> {
	static tableName = 'sets';

	static columns = [
		'id',
		'episodic_id',
		'series_set_id',
		'mobile_id',
		'number',
		'int_ext',
		'name',
		'description',
		'type_id',
		'last_updated_time',
		'last_updated_user',
		'date_created',
		'created_by',
	];

	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	episodic_id: number;

	@Column('int')
	series_set_id: number;

	@Column('varchar')
	mobile_id: string;

	@Column('varchar')
	number: string;

	@Column('int')
	int_ext: INT_EXT;

	@Column('varchar')
	name: string;

	@Column('varchar')
	description: string;

	@Column('int')
	type_id: SET_TYPES;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@Column('int')
	created_by: number;

	@OneToMany((type) => SetProd, (setProd) => setProd.set)
	setProds: SetProd[];

	@ManyToOne((type) => Episodic, (episodic) => episodic.sets)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@OneToMany((type) => SetPicture, (setPicture) => setPicture.set)
	setPictures: SetPicture[];

	@OneToMany((type) => SetPieceSet, (setPieceSet) => setPieceSet.set)
	setPieceSets: SetPieceSet[];

	@OneToMany((type) => SetScene, (setScene) => setScene.set)
	setScenes: SetScene[];

	@OneToMany((type) => SetDate, (setScene) => setScene.set)
	setDates: SetDate[];

	@ManyToMany(() => SetPiece, (setPiece) => setPiece.sets)
	setPieces: SetPiece[];

	static rules() {
		return {
			id: Joi.number().integer(),
			episodic_id: Joi.number().integer(),
			series_set_id: Joi.number().integer().allow(null),
			mobile_id: Joi.string().max(36).allow(null, ''),
			number: Joi.string().max(10).allow(null),
			int_ext: Joi.number().integer().allow(null),
			name: Joi.string().max(1000),
			description: Joi.string().max(1000).allow(null),
			type_id: Joi.number().integer().allow(null),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			date_created: Joi.date().allow(null),
			created_by: Joi.number().integer(),
			scene_ids: Joi.array().items(Joi.number().integer()).allow(null),
		};
	}

	toJSON() {
		return {
			...super.toJSON(),
			series_set_id: this.series_set_id,
			mobile_id: this.mobile_id,
			number: this.number,
			int_ext: this.int_ext,
			name: this.name,
			description: this.description,
			type_id: this.type_id,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			date_created: this.date_created,
			created_by: this.created_by,
		};
	}

	toESDoc() {
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			name: this.getFullName(),
		};
	}

	assign(props?: GenPartial<Set>) {
		super.assign(props);

		if (props) {
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<Set>) {
		super(props);
	}

	getFullName(): string {
		let name = '';
		if (this.number) {
			name += `${this.number} - `;
		}

		if (this.int_ext) {
			name += `${displayIntExt(this.int_ext)}. `;
		}

		return `${name}${this.name}`;
	}
	getShootTimesForCurrentProd(includeOmit = true, prodId = null): string {
		let shootTimes: any = [];
		for (const setScene of this.setScenes) {
			let scene = setScene.scene;
			if (scene.prod_id == prodId && (Number(scene.omit) == 0 || includeOmit)) {
				let shootTime = scene.getdecodeShootTime();
				if (shootTime) {
					if (!shootTimes.includes(shootTime)) {
						shootTimes.push(shootTime);
					}
				}
			}
		}
		return shootTimes.toString().trim().replace(/,/g, ', ');
	}

	getTotalSceneLengthForCurrentProd(includeOmit = true, prodId = null) {
		let totalEights = 0;
		for (const setScene of this.setScenes) {
			let scene = setScene.scene;
			if (scene.prod_id == prodId && (Number(scene.omit) == 0 || includeOmit)) {
				totalEights += scene.page_length ? scene.page_length : 0;
			}
		}
		return this.pageLengthString(totalEights);
	}
	pageLengthString(eighths) {
		let whole, numerator: any;
		if (eighths > 7) {
			whole = Math.floor(eighths / 8);
			numerator = eighths % 8;
		} else {
			numerator = eighths;
			whole = 0;
		}
		let wh = whole ? whole : '';
		let flo = numerator ? numerator + '/8' : '';
		if (wh && flo) {
			return wh + ' ' + flo;
		} else {
			return wh + flo ? wh + flo : 0;
		}
	}

	getTagName() {
		return this.getFullName();
	}
}
